import { useTranslation } from 'react-i18next';
import { Ref, useState } from 'react';
import Box from '@mui/material/Box';
import { SessionHistoryHeaderRow } from 'simInventory/SessionHistory/Components/SessionHistoryHeaderRow.tsx';
import { SessionHistoryRowWithInterim } from 'simInventory/SessionHistory/Components/SessionHistoryRowWithInterim.tsx';
import CircularProgress from '@mui/material/CircularProgress';
import { EmptyTableMessage } from 'common/components/EmptyTableMessage.tsx';
import { palette } from 'theme/palette';
import { ScrollArea } from 'src/common/components/ScrollArea/ScrollArea';
import { pxToRem } from 'common/utils/fonts.ts';
import { ContentBox } from 'common/components/AppLayout/ContentBox.tsx';
import { TableSkeleton } from 'common/components/Table/TableSkeleton.tsx';
import { SessionHistory } from 'common/components/SessionHistory/utils/sessions.ts';

type SessionHistoryTableProps = {
  sessionHistory: SessionHistory;
  isLoading: boolean;
  showLoader: boolean;
  fetchingEnded: boolean;
  refToLoader?: Ref<HTMLDivElement>;
  columnsCount: number;
} & (
  | {
      showProfile: true;
      primarySimImsi: string;
    }
  | {
      showProfile: false | undefined;
    }
);

export const SessionHistoryTable = ({
  sessionHistory,
  columnsCount,
  isLoading,
  showLoader,
  fetchingEnded,
  refToLoader,
  ...additionalProps
}: SessionHistoryTableProps) => {
  const { t } = useTranslation();

  const getOpenedRowsMap = () => {
    return (
      sessionHistory.reduce((current, item) => {
        return { ...current, [item.id]: false };
      }, {}) || {}
    );
  };
  const [openedRows, setOpenedRows] = useState<Record<string, boolean>>(getOpenedRowsMap());

  const collapseAllButtonVisible = Object.values(openedRows).some(Boolean);

  const spanProp = `span ${columnsCount + 2} `;

  return isLoading ? (
    <ContentBox>
      <TableSkeleton numberOfRows={20} pagination={false} />
    </ContentBox>
  ) : (
    <Box
      sx={{
        backgroundColor:
          sessionHistory.length === 0 ? palette.background.paper : palette.background.default,
        height: '100%',
      }}
    >
      <ScrollArea
        style={{
          position: 'absolute',
          inset: 0,
        }}
      >
        <Box
          sx={{
            height: '100%',
            backgroundColor:
              sessionHistory.length === 0 ? palette.background.paper : palette.background.default,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `${pxToRem(48)} repeat(${columnsCount}, max-content) auto`,
            }}
          >
            <SessionHistoryHeaderRow
              collapseAllButtonVisible={collapseAllButtonVisible}
              onCollapseAllClick={() => setOpenedRows(getOpenedRowsMap())}
              showProfile={additionalProps.showProfile}
            />
            <Box
              sx={{
                display: 'grid',
                gridColumn: spanProp,
                gridTemplateColumns: 'subgrid',
                borderBottom: (theme) => `1px solid ${theme.palette.border}`,
                '&>*': {
                  borderLeft: (theme) => `1px solid ${theme.palette.border}`,
                  borderRight: (theme) => `1px solid ${theme.palette.border}`,
                },
                '&>div:first-child': {
                  borderTop: 'none',
                },
                '&>.opened': {
                  borderBottom: (theme) => `1px solid ${theme.palette.border}`,
                },
                '&>* + .opened': {
                  mt: 12,
                },
                '&>*:has(+  .opened)': {
                  borderBottom: (theme) => `1px solid ${theme.palette.border}`,
                },
                '&>.opened + *': {
                  mt: 12,
                },
                '&>*+*': {
                  borderTop: (theme) => `1px solid ${theme.palette.border}`,
                },
              }}
            >
              {sessionHistory.map((row) => {
                return (
                  <SessionHistoryRowWithInterim
                    {...row}
                    imsi={row.imsi}
                    opened={openedRows?.[row.id] || false}
                    onToggle={(open: boolean) => {
                      setOpenedRows((currentVal) => ({ ...currentVal, [row.id]: open }));
                    }}
                    showProfile={additionalProps.showProfile}
                    isPrimary={
                      additionalProps.showProfile &&
                      row.imsi + '' === additionalProps.primarySimImsi
                    }
                  />
                );
              })}
              {showLoader && (
                <Box
                  sx={{
                    py: 8,
                    gridColumn: spanProp,
                    background: (theme) => theme.palette.background.paper,
                  }}
                >
                  <div ref={refToLoader}>
                    <Box
                      sx={{
                        position: 'sticky',
                        left: '50vw',
                        width: 'min-content',
                        transform: 'translateX(-50%)',
                      }}
                    >
                      <CircularProgress color="inherit" />
                    </Box>
                  </div>
                </Box>
              )}
            </Box>
            {sessionHistory.length === 0 && fetchingEnded ? (
              <Box
                sx={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  width: 'min-content',
                  transform: 'translateX(-50%) translateY(-50%)',
                }}
              >
                <EmptyTableMessage message={t('common.noResults')} />
              </Box>
            ) : null}
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
};
